import React, { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import moment from "moment";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { themeQuartz } from "ag-grid-community";

import { IDog, IBarnHuntProgress, IDogRunSummary } from "@eagerdog/interfaces";
import { Constants, BARN_HUNT_LEVEL_LABELS, BARN_HUNT_RESULT_LABELS } from "@eagerdog/constants";

import Button from "../../../components/Button/Button";
import { Modal, useModal } from "../../../components/Modal/Modal";
import { ManualRunEntryModal } from "../ManualRunEntry";
import { apiService } from "../../../services/api.service";
import { toast } from "../../../components/Toast/ToastManager";
import ContextMenu, { IContextMenuOption } from "../../../components/ContextMenu/ContextMenu";

import "./BarnHuntCard.scss";

interface IProps {
  dog: IDog;
  barnHuntData: IBarnHuntProgress;
  onRunUpdated?: () => void;
}

const BarnHuntCard: React.FC<IProps> = ({ dog, barnHuntData, onRunUpdated }) => {
  const [showRunsModal, setShowRunsModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRun, setEditingRun] = useState<IDogRunSummary | undefined>(undefined);
  const [modalKey, setModalKey] = useState<string>(() => Date.now().toString());
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);
  const [runToDelete, setRunToDelete] = useState<string | null>(null);
  const [localRuns, setLocalRuns] = useState<IDogRunSummary[]>([]);
  const needsParentRefreshRef = useRef(false);
  
const deleteConfirmModal = useModal();

  // Format runs for AgGrid
  useEffect(() => {
    if (barnHuntData.recent_runs) {
      setLocalRuns(barnHuntData.recent_runs);
    }
  }, [barnHuntData.recent_runs]);

  const handleCloseRunsModal = () => {
    setShowRunsModal(false);
    
    if (needsParentRefreshRef.current) {
      needsParentRefreshRef.current = false;
      onRunUpdated?.();
    }
  };

  const handleAddManualRun = () => {
    const newKey = Date.now().toString();
    setEditingRun(undefined);
    setModalKey(newKey);
    setIsModalOpen(true);
  };

  const handleEditRun = (run: IDogRunSummary) => {
    // recent_runs already use the shared IDogRunSummary shape (result_status holds
    // the Barn Hunt score_result_type value), so the run can be edited as-is.
    setEditingRun(run);
    setModalKey(`edit-${run._id}`);
    setIsModalOpen(true);
  };

  const handleDeleteRun = (runId: string) => {
    setRunToDelete(runId);
    deleteConfirmModal.toggle();
  };

  // Stable refs for AG Grid cell renderer callbacks (avoids stale closure issues)
  const handleEditRunRef = useRef(handleEditRun);
  const handleDeleteRunRef = useRef(handleDeleteRun);
  const deletingRunIdRef = useRef(deletingRunId);
  useEffect(() => { handleEditRunRef.current = handleEditRun; });
  useEffect(() => { handleDeleteRunRef.current = handleDeleteRun; });
  useEffect(() => { deletingRunIdRef.current = deletingRunId; }, [deletingRunId]);

  const confirmDelete = async () => {
    if (!runToDelete) return;
    
    setDeletingRunId(runToDelete);
    
    try {
      await apiService.deleteManualRun(runToDelete);
      
      toast.show({
        title: "Run Deleted",
        content: "The run has been deleted and title progress has been updated.",
        duration: 5000,
        type: "success"
      });
      
      needsParentRefreshRef.current = true;
      
      if (onRunUpdated) {
        await onRunUpdated();
      }
    } catch (error: any) {
      toast.show({
        title: "Error",
        content: error?.message || "Failed to delete run",
        duration: 5000,
        type: "error"
      });
    } finally {
      setDeletingRunId(null);
      setRunToDelete(null);
      deleteConfirmModal.toggle();
    }
  };

  const getActionMenuOptions = (run: IDogRunSummary) => {
    if (!run) return null;
    const isManualEntry = run.data_source === Constants.run_data_source.manual_entry;
    if (run.verified) return null;
    if (deletingRunIdRef.current === run._id) return null;
    if (!isManualEntry) return null;
    return [
      { label: 'Edit', onClick: () => handleEditRunRef.current(run) },
      { label: 'Delete', onClick: () => handleDeleteRunRef.current(run._id) }
    ] as IContextMenuOption[];
  };

  const ActionsCellRenderer = (params: any) => {
    const run = params.data;
    if (!run) return null;
    if (run.verified) return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#10b981', fontWeight: 600 }}>Verified</span>;
    if (deletingRunIdRef.current === run._id) return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>Deleting...</span>;
    const options = params.getOptions(run);
    if (!options) return null;
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><ContextMenu options={options} disablePortal /></div>;
  };

  // AGGrid column definitions
  const columnDefs: ColDef[] = [
    {
      headerName: 'Date',
      field: 'trial_date',
      valueFormatter: (params) => moment(params.value).format('M/D/YYYY'),
      minWidth: 120,
      flex: 1
    },
    {
      headerName: 'Level',
      field: 'level',
      valueFormatter: (params) => {
        const level = params.value || '';
        return BARN_HUNT_LEVEL_LABELS[level] || level;
      },
      minWidth: 150,
      flex: 1
    },
    {
      headerName: 'Section',
      field: 'section',
      valueFormatter: (params) => params.value || 'A',
      minWidth: 100,
      flex: 1
    },
    {
      headerName: 'Result',
      field: 'result_status',
      valueFormatter: (params) => {
        const result = params.value || '';
        return BARN_HUNT_RESULT_LABELS[result] || result;
      },
      minWidth: 120,
      flex: 1,
      cellStyle: (params) => {
        if (params.value === Constants.score_result_type.qualified) return { color: '#10b981', fontWeight: 600 };
        if (params.value === Constants.score_result_type.not_qualified) return { color: '#ef4444', fontWeight: 600 };
        return null;
      }
    },
    {
      headerName: 'Source',
      valueGetter: (params) => params.data.data_source === Constants.run_data_source.manual_entry ? 'Manual Entry' : 'Event',
      minWidth: 150,
      flex: 1
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      cellRendererParams: { getOptions: getActionMenuOptions },
      cellStyle: { overflow: 'visible', zIndex: 1 },
      minWidth: 150,
      flex: 1
    }
  ];

  const handleModalSuccess = async () => {
    setIsModalOpen(false);
    setEditingRun(undefined);
    needsParentRefreshRef.current = true;
    
    // Immediately refresh the data
    if (onRunUpdated) {
      await onRunUpdated();
    }
    
    toast.show({
      title: "Run Saved",
      content: "The run has been saved and title progress has been updated.",
      duration: 5000,
      type: "success"
    });
  };
  
  /**
   * Render a title badge with earned date
   */
  const renderTitleBadge = (title: { title_code: string; title_name: string; earned_date: Date; color_hex?: string }) => {
    return (
      <div
        key={title.title_code}
        className="titleBadge"
        style={{ backgroundColor: title.color_hex || '#10b981' }}
        title={`${title.title_name} - Earned ${moment(title.earned_date).format('M/D/YYYY')}`}
      >
        <span className="titleCode">{title.title_code}</span>
        <span className="earnedDate">{moment(title.earned_date).format('M/D/YY')}</span>
      </div>
    );
  };

  /**
   * Render progress bar for the next title
   */
  const renderProgressBar = () => {
    // Next title is computed server-side (IBarnHuntProgress.next_regular_title)
    // so the web card and the mobile card always agree.
    const next = barnHuntData.next_regular_title;

    if (!next) {
      return (
        <div className="progressSection">
          <div className="allTitlesEarned">🎉 All Major Titles Earned!</div>
        </div>
      );
    }

    return (
      <div className="progressSection">
        <div className="progressHeader">
          <span className="progressTitle">Next Title: {next.code} - {next.name}</span>
          <span className="progressCount">
            {next.current} / {next.required} {next.progress_type}
          </span>
        </div>
        <div className="progressBar">
          <div className="progressFill" style={{ width: `${next.percentage}%` }} />
        </div>
      </div>
    );
  };

  /**
   * Render level detail section
   */
  const renderLevelDetail = (
    levelName: string,
    levelData: {
      title_code: string;
      title_name: string;
      title_earned: boolean;
      qualified_count: number;
      required_legs: number;
      unique_judges: number;
      required_judges: number;
    } | undefined
  ) => {
    if (!levelData) return null;

    return (
      <div className={classNames("levelDetail", { earned: levelData.title_earned })}>
        <div className="levelName">{levelName}</div>
        <div className="levelStats">
          <span>{levelData.qualified_count}/{levelData.required_legs} legs</span>
          <span>{levelData.unique_judges}/{levelData.required_judges} judges</span>
          {levelData.title_earned && <span className="earned">✓ Earned</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="barnHuntCard">
      <div className="cardHeader">
        <div className="headerLeft">
          <h3>BHA Barn Hunt</h3>
          <p className="cardSubtitle">Barn Hunt Association</p>
        </div>
        <div className="headerRight">
          <div className="statsGrid">
            <div className="statItem">
              <div className="statLabel">Total Runs</div>
              <div className="statValue">{barnHuntData.total_trials || 0}</div>
            </div>
            <div className="statItem">
              <div className="statLabel">Qualified</div>
              <div className="statValue">{barnHuntData.total_qualified || 0}</div>
            </div>
            <div className="statItem">
              <div className="statLabel">Titles Earned</div>
              <div className="statValue">{barnHuntData.total_titles_earned || 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Earned Titles */}
      {barnHuntData.earned_titles && barnHuntData.earned_titles.length > 0 && (
        <div className="earnedTitlesSection">
          <h4>Earned Titles ({barnHuntData.earned_titles.length})</h4>
          <div className="titleBadges">
            {barnHuntData.earned_titles.map(renderTitleBadge)}
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {renderProgressBar()}

      {/* Last Run Info */}
      {barnHuntData.last_trial_date && (
        <div className="lastRunSection">
          <span className="lastRunLabel">Last Trial:</span>
          <span className="lastRunValue">{moment(barnHuntData.last_trial_date).format('M/D/YYYY')}</span>
        </div>
      )}

      {/* Level Details */}
      <div className="levelDetailsSection">
        <h4>Progress by Level</h4>
        <div className="levelsGrid">
          {renderLevelDetail("Instinct", barnHuntData.instinct)}
          {renderLevelDetail("Novice A", barnHuntData.novice)}
          {renderLevelDetail("Novice X (B)", barnHuntData.novice_x)}
          {renderLevelDetail("Open A", barnHuntData.open)}
          {renderLevelDetail("Open X (B)", barnHuntData.open_x)}
          {renderLevelDetail("Senior A", barnHuntData.senior)}
          {renderLevelDetail("Senior X (B)", barnHuntData.senior_x)}
          {renderLevelDetail("Master", barnHuntData.master)}
          {renderLevelDetail("Champion", barnHuntData.champion)}
          {renderLevelDetail("Champion X", barnHuntData.champion_x)}
          
          {/* REMX (with time display) */}
          {barnHuntData.elite_master && barnHuntData.elite_master.qualified_count > 0 && (
            <div className={classNames("levelDetail", { earned: barnHuntData.elite_master.title_earned })}>
              <div className="levelName">Elite Master Champion (REMX)</div>
              <div className="levelStats">
                <span>{barnHuntData.elite_master.qualified_count}/{barnHuntData.elite_master.required_legs} legs</span>
                {barnHuntData.elite_master.average_time_formatted && (
                  <span>Avg: {barnHuntData.elite_master.average_time_formatted}</span>
                )}
                {barnHuntData.elite_master.title_earned && <span className="earned">✓ Earned</span>}
              </div>
            </div>
          )}

          {/* Crazy 8s */}
          {barnHuntData.crazy8s && barnHuntData.crazy8s.total_points > 0 && (
            <div className="levelDetail special">
              <div className="levelName">Crazy 8s</div>
              <div className="levelStats">
                <span>{barnHuntData.crazy8s.total_points} points</span>
                {barnHuntData.crazy8s.highest_title && (
                  <span className="earned">{barnHuntData.crazy8s.highest_title}</span>
                )}
              </div>
            </div>
          )}

          {/* Line Drive */}
          {barnHuntData.line_drive && barnHuntData.line_drive.qualified_count > 0 && (
            <div className={classNames("levelDetail", "special", { earned: barnHuntData.line_drive.title_earned })}>
              <div className="levelName">Line Drive</div>
              <div className="levelStats">
                <span>{barnHuntData.line_drive.qualified_count}/{barnHuntData.line_drive.required_legs} legs</span>
                {barnHuntData.line_drive.title_earned && <span className="earned">✓ Earned</span>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="summarySection">
        <div className="summaryItem">
          <span className="summaryLabel">Highest Level Competed:</span>
          <span className="summaryValue">{barnHuntData.highest_level_competed || 'N/A'}</span>
        </div>
        <div className="summaryItem">
          <span className="summaryLabel">Highest Level Qualified:</span>
          <span className="summaryValue">{barnHuntData.highest_level_qualified || 'N/A'}</span>
        </div>
      </div>

      {/* View Runs Button */}
      {onRunUpdated ? (
        <div className="viewRunsSection">
          <button 
            className="viewRunsLink"
            onClick={() => setShowRunsModal(true)}
          >
            View & Edit Run History ({barnHuntData.recent_runs?.length || 0} runs)
          </button>
        </div>
      ) : barnHuntData.recent_runs && barnHuntData.recent_runs.length > 0 ? (
        <div className="viewRunsSection">
          <button 
            className="viewRunsLink"
            onClick={() => setShowRunsModal(true)}
          >
            View Run History ({barnHuntData.recent_runs.length} runs)
          </button>
        </div>
      ) : null}

      {/* Runs Modal */}
      <Modal
        isShown={showRunsModal}
        hide={handleCloseRunsModal}
        className="runsHistoryModal"
        modalContent={
          <div className="runsModal">
            <h2>BHA Barn Hunt - Run History</h2>

            <div className="runsModalContent">
              {/* Runs Table */}
              <div className="runsSection">
                <div className="runsSectionHeader">
                  <h4>All Runs</h4>
                </div>
                {localRuns && localRuns.length > 0 ? (
                  <div className="ag-theme-quartz" style={{ height: 400, width: '100%' }}>
                    <AgGridReact
                      rowData={localRuns}
                      columnDefs={columnDefs}
                      domLayout='normal'
                      suppressCellFocus={true}
                      theme={themeQuartz}
                      getRowId={(params) => params.data._id}
                    />
                  </div>
                ) : (
                  <p>No runs recorded yet.</p>
                )}
              </div>

              {/* Add Manual Run Button - only show for owners */}
              {onRunUpdated && (
                <div className="modalActions">
                  <Button onClick={handleAddManualRun}>
                    + Add Manual Run
                  </Button>
                </div>
              )}
            </div>
          </div>
        }
      />

      {/* Manual Run Entry Modal */}
      <ManualRunEntryModal
        key={modalKey}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingRun(undefined);
        }}
        dog={dog}
        sportType={Constants.show_type.barn_hunt}
        sanctioningClub={Constants.sanctioning_club.BHA}
        onSuccess={handleModalSuccess}
        editRun={editingRun as any}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isShown={deleteConfirmModal.isShown}
        hide={deleteConfirmModal.toggle}
        className="deleteConfirmModal"
        modalContent={
          <div className="deleteConfirmModal">
            <h2>Delete Run</h2>
            <p>Are you sure you want to delete this run? This action cannot be undone.</p>
            <div className="modalActions">
              <Button onClick={deleteConfirmModal.toggle} variant="secondary">
                Cancel
              </Button>
              <Button onClick={confirmDelete} className="deleteButton">
                Delete
              </Button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default BarnHuntCard;

